import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import '../styles/BotActivationHistory.css'; // Import CSS file

const API_BASE = "https://api.mbsmartweb.com/api";

const BotActivationHistory = () => {
  const [activations, setActivations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [selectedActivation, setSelectedActivation] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    totalAmount: 0
  });
  
  const currentUserId = localStorage.getItem("UserId");

  useEffect(() => {
    if (currentUserId) {
      fetchBotActivations();
    }
  }, [currentUserId]);

  const fetchBotActivations = async () => {
    try {
      setLoading(true);
      // Fetch bot activations where current user is the activator
      const response = await axios.post(`${API_BASE}/bot-activations-by-user`, {
        ActivatedBy: currentUserId
      });
      
      if (response.data.success) {
        setActivations(response.data.data);
        calculateStats(response.data.data);
      } else {
        setActivations([]);
        if (response.data.error !== "No activations found") {
          toast.info(response.data.error);
        }
      }
    } catch (err) {
      console.error("Error fetching bot activations:", err);
      toast.error("Failed to load bot activation history");
      setActivations([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const now = new Date();
    const active = data.filter(item => {
      return item.Status === 'ACTIVE' && new Date(item.EndDate) > now;
    }).length;
    
    const expired = data.filter(item => {
      return item.Status === 'EXPIRED' || new Date(item.EndDate) <= now;
    }).length;
    
    const totalAmount = data.reduce((sum, item) => sum + item.Amount, 0);
    
    setStats({
      total: data.length,
      active: active,
      expired: expired,
      totalAmount: totalAmount
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today ' + date.toLocaleTimeString();
    if (diffDays === 1) return 'Yesterday ' + date.toLocaleTimeString();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatusBadge = (status, endDate) => {
    const now = new Date();
    const expiry = new Date(endDate);
    
    if (status === 'ACTIVE' && expiry > now) {
      return <span className="status-badge active"><i className="fa fa-circle"></i> Active</span>;
    } else if (status === 'EXPIRED' || expiry <= now) {
      return <span className="status-badge expired"><i className="fa fa-circle"></i> Expired</span>;
    }
    return <span className="status-badge inactive"><i className="fa fa-circle"></i> Inactive</span>;
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const now = new Date();
    const expiry = new Date(endDate);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Expires today';
    if (diffDays === 1) return '1 day remaining';
    return `${diffDays} days remaining`;
  };

  const getFilteredActivations = () => {
    let filtered = [...activations];
    
    // Filter by status
    if (filterStatus !== 'all') {
      const now = new Date();
      if (filterStatus === 'active') {
        filtered = filtered.filter(item => 
          item.Status === 'ACTIVE' && new Date(item.EndDate) > now
        );
      } else if (filterStatus === 'expired') {
        filtered = filtered.filter(item => 
          item.Status === 'EXPIRED' || new Date(item.EndDate) <= now
        );
      }
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.Userid.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.PackageName && item.PackageName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        item.TransactionId?.toString().includes(searchTerm)
      );
    }
    
    // Sort
    switch(sortBy) {
      case 'date_desc':
        filtered.sort((a, b) => new Date(b.ActivationDate) - new Date(a.ActivationDate));
        break;
      case 'date_asc':
        filtered.sort((a, b) => new Date(a.ActivationDate) - new Date(b.ActivationDate));
        break;
      case 'amount_desc':
        filtered.sort((a, b) => b.Amount - a.Amount);
        break;
      case 'amount_asc':
        filtered.sort((a, b) => a.Amount - b.Amount);
        break;
      case 'user_asc':
        filtered.sort((a, b) => a.Userid.localeCompare(b.Userid));
        break;
      default:
        filtered.sort((a, b) => new Date(b.ActivationDate) - new Date(a.ActivationDate));
    }
    
    return filtered;
  };

  const filteredActivations = getFilteredActivations();

  const exportToCSV = () => {
    const headers = ['ID', 'User ID', 'Amount', 'Package', 'Start Date', 'End Date', 'Status', 'Activation Date'];
    const csvData = filteredActivations.map(item => [
      item.Id,
      item.Userid,
      item.Amount,
      item.PackageName || `Package ${item.PackageId}`,
      new Date(item.StartDate).toLocaleString(),
      new Date(item.EndDate).toLocaleString(),
      item.Status,
      new Date(item.ActivationDate).toLocaleString()
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bot_activations_${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully!');
  };

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      
      <div className="bot-history-page">
        <div className="page-header">
          <h1>
            <i className="fa fa-robot"></i>
            Bot Activation History
          </h1>
          <p>View all bot activations you have performed</p>
        </div>

        {/* Statistics Cards */}
        <div className="stats-cards">
          <div className="stat-card total">
            <div className="stat-icon">
              <i className="fa fa-chart-line"></i>
            </div>
            <div className="stat-info">
              <span className="stat-label">Total Activations</span>
              <span className="stat-value">{stats.total}</span>
            </div>
          </div>
          
          <div className="stat-card active">
            <div className="stat-icon">
              <i className="fa fa-check-circle"></i>
            </div>
            <div className="stat-info">
              <span className="stat-label">Active Bots</span>
              <span className="stat-value">{stats.active}</span>
            </div>
          </div>
          
          <div className="stat-card expired">
            <div className="stat-icon">
              <i className="fa fa-clock"></i>
            </div>
            <div className="stat-info">
              <span className="stat-label">Expired Bots</span>
              <span className="stat-value">{stats.expired}</span>
            </div>
          </div>
          
          <div className="stat-card amount">
            <div className="stat-icon">
              <i className="fa fa-dollar-sign"></i>
            </div>
            <div className="stat-info">
              <span className="stat-label">Total Amount</span>
              <span className="stat-value">{formatAmount(stats.totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-box">
            <i className="fa fa-search"></i>
            <input
              type="text"
              placeholder="Search by User ID, Package..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="expired">Expired Only</option>
            </select>
          </div>
          
          <div className="filter-group">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="date_desc">Newest First</option>
              <option value="date_asc">Oldest First</option>
              <option value="amount_desc">Highest Amount</option>
              <option value="amount_asc">Lowest Amount</option>
              <option value="user_asc">User ID A-Z</option>
            </select>
          </div>
          
          <button className="btn-export" onClick={exportToCSV}>
            <i className="fa fa-download"></i>
            Export CSV
          </button>
          
          <button className="btn-refresh" onClick={fetchBotActivations}>
            <i className="fa fa-refresh"></i>
            Refresh
          </button>
        </div>

        {/* Activations Table */}
        <div className="activations-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading bot activations...</p>
            </div>
          ) : filteredActivations.length === 0 ? (
            <div className="empty-state">
              <i className="fa fa-inbox"></i>
              <p>No bot activations found</p>
              <p className="empty-subtitle">You haven't activated any bots yet</p>
              <button className="btn-primary" onClick={() => window.location.href='/activate'}>
                Activate Bot Now
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="activations-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User ID</th>
                    <th>Amount</th>
                    <th>Package</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Days Left</th>
                    <th>Status</th>
                    <th>Activation Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivations.map((activation) => (
                    <tr key={activation.Id}>
                      <td className="id">#{activation.Id}</td>
                      <td className="user-id">{activation.Userid}</td>
                      <td className="amount">{formatAmount(activation.Amount)}</td>
                      <td className="package">
                        <span className="package-badge">
                          {activation.PackageName || `Package ${activation.PackageId}`}
                        </span>
                      </td>
                      <td className="date">{formatDate(activation.StartDate)}</td>
                      <td className="date">{formatDate(activation.EndDate)}</td>
                      <td className="days-left">
                        <span className={`days-badge ${getDaysRemaining(activation.EndDate) === 'Expired' ? 'expired' : 'active'}`}>
                          {getDaysRemaining(activation.EndDate)}
                        </span>
                      </td>
                      <td>{getStatusBadge(activation.Status, activation.EndDate)}</td>
                      <td className="date">{formatDate(activation.ActivationDate)}</td>
                      <td className="actions">
                        <button 
                          className="btn-view"
                          onClick={() => setSelectedActivation(activation)}
                          title="View Details"
                        >
                          <i className="fa fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination (if needed) */}
        {filteredActivations.length > 0 && (
          <div className="pagination">
            <span className="pagination-info">
              Showing {filteredActivations.length} of {activations.length} activations
            </span>
          </div>
        )}

        {/* Modal for Details */}
        {selectedActivation && (
          <div className="modal-overlay" onClick={() => setSelectedActivation(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>
                  <i className="fa fa-info-circle"></i>
                  Bot Activation Details
                </h3>
                <button onClick={() => setSelectedActivation(null)}>&times;</button>
              </div>
              <div className="modal-body">
                <div className="detail-row">
                  <span className="detail-label">Activation ID:</span>
                  <strong>#{selectedActivation.Id}</strong>
                </div>
                <div className="detail-row">
                  <span className="detail-label">User ID:</span>
                  <strong className="user-id">{selectedActivation.Userid}</strong>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Amount:</span>
                  <strong className="amount-highlight">{formatAmount(selectedActivation.Amount)}</strong>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Package:</span>
                  <strong>{selectedActivation.PackageName || `Package ${selectedActivation.PackageId}`}</strong>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Package ID:</span>
                  <strong>{selectedActivation.PackageId}</strong>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Start Date:</span>
                  <strong>{new Date(selectedActivation.StartDate).toLocaleString()}</strong>
                </div>
                <div className="detail-row">
                  <span className="detail-label">End Date:</span>
                  <strong>{new Date(selectedActivation.EndDate).toLocaleString()}</strong>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Days Remaining:</span>
                  <strong className={getDaysRemaining(selectedActivation.EndDate) === 'Expired' ? 'expired-text' : 'active-text'}>
                    {getDaysRemaining(selectedActivation.EndDate)}
                  </strong>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <div>{getStatusBadge(selectedActivation.Status, selectedActivation.EndDate)}</div>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Activated By:</span>
                  <strong>{selectedActivation.ActivatedBy}</strong>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Activation Date:</span>
                  <strong>{new Date(selectedActivation.ActivationDate).toLocaleString()}</strong>
                </div>
                {selectedActivation.TransactionId && (
                  <div className="detail-row">
                    <span className="detail-label">Transaction ID:</span>
                    <strong>{selectedActivation.TransactionId}</strong>
                  </div>
                )}
                {selectedActivation.Remarks && (
                  <div className="detail-row">
                    <span className="detail-label">Remarks:</span>
                    <strong>{selectedActivation.Remarks}</strong>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-close" onClick={() => setSelectedActivation(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BotActivationHistory;